import { defineStore } from "pinia";
import { computed, ref } from "vue";

const STORAGE_KEYS = {
  TOKEN: "lf_platform_token",
  ADMIN: "lf_platform_admin",
};

export interface PlatformAdminProfile {
  id: string;
  email: string;
  name: string | null;
}

function safeJsonParse<T>(raw: string | null): T | null {
  if (!raw) return null;
  try {
    return JSON.parse(raw) as T;
  } catch {
    return null;
  }
}

/**
 * 平台管理员登录态（跨租户身份）。
 *
 * ⚠️ 它**不是**租户登录态：租户用户走 JWT（`stores/auth.ts`），
 *    平台管理员走**独立会话令牌**（`Authorization: Bearer <token>`）。
 *    两套身份互不通用 —— 租户令牌在平台接口上必然 403，反之亦然。
 *
 * ⚠️ 令牌存 localStorage 而不是内存：刷新页面不用重登。
 *    风险与租户 JWT 相同（XSS 能读到），真正该防的是"令牌进了前端代码仓库"。
 *    服务端支持登出（`POST /v1/platform/logout` 会真的作废这个令牌），
 *    所以怀疑泄露时可以立刻作废 —— 这比不可撤销的 JWT 更适合高权限账号。
 */
export const usePlatformStore = defineStore("platform", () => {
  const token = ref<string>(localStorage.getItem(STORAGE_KEYS.TOKEN) || "");
  const admin = ref<PlatformAdminProfile | null>(
    safeJsonParse<PlatformAdminProfile>(localStorage.getItem(STORAGE_KEYS.ADMIN)),
  );

  const isLoggedIn = computed(() => token.value.length > 0);

  function setSession(nextToken: string, profile: PlatformAdminProfile): void {
    token.value = nextToken;
    admin.value = profile;
    localStorage.setItem(STORAGE_KEYS.TOKEN, nextToken);
    localStorage.setItem(STORAGE_KEYS.ADMIN, JSON.stringify(profile));
  }

  function clear(): void {
    token.value = "";
    admin.value = null;
    localStorage.removeItem(STORAGE_KEYS.TOKEN);
    localStorage.removeItem(STORAGE_KEYS.ADMIN);
  }

  return { token, admin, isLoggedIn, setSession, clear };
});
